import * as React from "react";
import { useEffect, useRef } from "react";

// Constants
const CONFIRMATION_MODAL_ID = "confirmation-modal";

// Context Types
interface ConfirmationModalContextProps {
  showConfirmation: ({
    title,
    message,
    primaryAction,
    options,
  }: {
    title?: string;
    message: React.ReactNode;
    primaryAction?: () => void;
    options?: Partial<ConfirmationModalOptions>;
  }) => void;
  hideConfirmation: () => void;
}

interface IButtonsProps {
  text: string;
  tone: "critical" | "default";
  variant: "primary" | "breadcrumb" | null | undefined;
}

interface ConfirmationModalOptions {
  title: string;
  showCautionMessage: boolean;
  primaryButton: Partial<IButtonsProps>;
  secondaryButton: Partial<IButtonsProps>;
}

const defaultOptions: ConfirmationModalOptions = {
  title: "Unsaved Changes Detected",
  primaryButton: { text: "Discard Changes", variant: "primary" },
  secondaryButton: { text: "Cancel" },
  showCautionMessage: true,
};

// Context
const ConfirmationModalContext =
  React.createContext<ConfirmationModalContextProps | null>(null);

// Provider
export const ConfirmationModalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [message, setMessage] = React.useState<React.ReactNode>(null);
  const [primaryAction, setPrimaryAction] = React.useState<
    (() => void) | undefined
  >(undefined);
  const [showCautionMessage, setShowCautionMessage] = React.useState(true);
  const [options, setOptions] =
    React.useState<ConfirmationModalOptions>(defaultOptions);

  // Use refs for DOM elements to avoid direct document calls during SSR
  const modalRef = useRef<HTMLElement | null>(null);

  // Set up refs after component mounts (client-side only)
  useEffect(() => {
    modalRef.current = document.getElementById(
      CONFIRMATION_MODAL_ID,
    ) as HTMLElement;
  }, []);

  const showConfirmation = ({
    title,
    message,
    primaryAction,
    options,
  }: {
    title?: string;
    message: React.ReactNode;
    primaryAction?: () => void;
    options?: Partial<ConfirmationModalOptions>;
  }) => {
    setMessage(message);
    setPrimaryAction(() => primaryAction);
    setOptions({ ...defaultOptions, ...options, title: title || defaultOptions.title });
    setShowCautionMessage(() => options?.showCautionMessage ?? true);

    // Only access DOM in client-side code
    if (typeof document !== "undefined" && modalRef.current) {
      // @ts-ignore-next-line
      modalRef.current.show();
    }
  };

  const hideConfirmation = () => {
    setMessage(null);
    setPrimaryAction(undefined);
    setOptions(defaultOptions);
    setShowCautionMessage(true);

    // Only access DOM in client-side code
    if (typeof document !== "undefined" && modalRef.current) {
      // @ts-ignore-next-line
      modalRef.current.hide();
    }
  };

  return (
    <ConfirmationModalContext.Provider
      value={{ showConfirmation, hideConfirmation }}
    >
      {children}
      <ui-modal id={CONFIRMATION_MODAL_ID}>
        <div
          className="modal-content"
          style={{
            padding: "2rem",
            margin: "0 auto",
          }}
        >
          <div className="alert-message">{message}</div>

          {showCautionMessage && (
            <p
              className="warning-message"
              style={{
                marginTop: "1rem",
              }}
            >
              If you continue, all unsaved changes will be permanently lost
            </p>
          )}
        </div>

        <ui-title-bar title={options.title}>
          <button
            variant={options.primaryButton.variant ?? "primary"}
            tone={options.primaryButton.tone ?? "default"}
            onClick={() => {
              primaryAction?.();
              hideConfirmation();
            }}
          >
            {options.primaryButton.text}
          </button>
          <button onClick={hideConfirmation}>
            {options.secondaryButton.text}
          </button>
        </ui-title-bar>
      </ui-modal>
    </ConfirmationModalContext.Provider>
  );
};

// Hook
export const useConfirmationModal = () => {
  const context = React.useContext(ConfirmationModalContext);
  if (!context) {
    throw new Error(
      "useConfirmationModal must be used within a ConfirmationModalProvider",
    );
  }
  return context;
};
